const smoothstep = (t) => t * t * (3 - 2 * t);

const ORGANIC_WOBBLE_RATIO = 0.25;
const ORGANIC_STABLE_WOBBLE_RATIO = 0.06;

const hashSeed = (previous, current) => {
  const key = `${previous}|${current}`;
  let seed = 0;

  for (let i = 0; i < key.length; i++) {
    seed = (seed * 33 + key.charCodeAt(i)) >>> 0;
  }

  return seed;
};

/** Deterministic 0..1 jitter — stable across re-renders for the same inputs. */
const seededUnit = (seed, index) => {
  const x = Math.sin((seed + index * 17.3) * 12.9898) * 43758.5453;
  return x - Math.floor(x);
};

const getWobbleIndices = (length, seed) => {
  if (length < 3) {
    return [];
  }

  const first = Math.max(1, Math.floor((length - 1) / 3));
  const second = Math.min(length - 2, Math.ceil((2 * (length - 1)) / 3));
  const useTwoPoints = seededUnit(seed, 999) > 0.45;

  if (!useTwoPoints || first === second) {
    return [first];
  }

  return [first, second];
};

const applyOrganicWobble = (values, previous, current) => {
  if (values.length < 3) {
    return values;
  }

  const delta = current - previous;
  const seed = hashSeed(previous, current);
  const absDelta = Math.abs(delta);
  const wobbleAmplitude =
    absDelta > 0
      ? absDelta * ORGANIC_WOBBLE_RATIO
      : Math.max(previous, current, 1) * ORGANIC_STABLE_WOBBLE_RATIO;

  const minVal = Math.min(previous, current);
  const maxVal = Math.max(previous, current);
  const wobbleIndices = new Set(getWobbleIndices(values.length, seed));

  return values.map((value, index) => {
    if (index === 0) {
      return previous;
    }

    if (index === values.length - 1) {
      return current;
    }

    if (!wobbleIndices.has(index)) {
      return value;
    }

    const offset = (seededUnit(seed, index) * 2 - 1) * wobbleAmplitude;
    let wobbled = value + offset;

    if (absDelta > 0) {
      wobbled = Math.max(minVal, Math.min(maxVal, wobbled));
    }

    return wobbled;
  });
};

/**
 * Interpolates a smooth sparkline between two period totals (previous → current).
 * Adds light deterministic wobble on 1–2 interior points for a more organic shape.
 * @param {number|string} previousValue
 * @param {number|string} currentValue
 * @param {number} [steps=5]
 * @returns {number[]}
 */
export const buildTwoPointSparkline = (
  previousValue,
  currentValue,
  steps = 5
) => {
  const previous = Number(previousValue) || 0;
  const current = Number(currentValue) || 0;

  if (previous === 0 && current === 0) {
    return [];
  }

  const baseValues = Array.from({ length: steps + 1 }, (_, i) => {
    const t = i / steps;
    return previous + (current - previous) * smoothstep(t);
  });

  return applyOrganicWobble(baseValues, previous, current);
};

export default buildTwoPointSparkline;
